import * as stylex from "@stylexjs/stylex";
import { format } from "d3-format";
import { defineMessage, useIntl } from "react-intl";
import { withDataLoader } from "../components/data-loader";
import { EmbedChart, StaticChart } from "../components/figure";
import { HeroControlsWrapper } from "../components/hero-controls/hero-controls";
import { Area } from "../components/hero-vis/area";
import { Hero } from "../components/hero-vis/hero";
import { Lines } from "../components/hero-vis/line";
import MultipleHeroes from "../components/multiple-heroes";
import { metadata } from "../data/data_finance_hero";
import { datasets } from "../data/data_finance_hero_datasets";
import { mkGemEntityDecoder, useCountryEntities, useNamedEntities, useRegionEntities } from "../domain";
import { foldChartType, HeroStateProvider, useChartType } from "../domain/hero-state";
import { useContentRect } from "../hooks";
import { layout } from "../materials/layout.stylex";
import { io } from "../prelude";
export * from "../data/data_finance_hero";
const formatValue = format(".1%");
const formatAxisValue = format(".0%");
const targetTitle = defineMessage({
    id: "fig.finance.HERO.targetTitle",
    defaultMessage: "Target"
});
const targetLabel = defineMessage({
    id: "fig.finance.HERO.target",
    defaultMessage: "Countries spend around 4% of their GDP on education"
});
const infoLabel = defineMessage({
    id: "fig.finance.HERO.info",
    defaultMessage: "Countries spend around 4% of their GDP on education"
});
export const Data = mkGemEntityDecoder([
    "ind_id",
    "year"
], {
    value: io.number
});
export const Chart = ({ data: dataRaw, width, csv, xlsx, renderMode, dataset })=>{
    const intl = useIntl();
    const indicatorLabel = intl.formatMessage(defineMessage({
        id: "fig.finance.HERO.indicatorLabel",
        defaultMessage: "GDP spent on education"
    }));
    const [chartType] = useChartType();
    const data = useNamedEntities(dataRaw);
    const countryData = useCountryEntities(data);
    const regionData = useRegionEntities(data);
    return <HeroControlsWrapper renderMode={renderMode} caption={intl.formatMessage(metadata.caption)} content={intl.formatMessage(infoLabel)} source={intl.formatMessage(metadata.source)} id={metadata.id} csv={csv} xlsx={xlsx} dataset={dataset} hideToggle>
      {foldChartType(chartType, {
        today: ()=><Area label={indicatorLabel} width={width} values={countryData} target={0.04} max={0.08} tickValuesY={[
                0,
                0.02,
                0.04,
                0.06,
                0.08
            ]} formatValue={formatValue} formatAxisValue={formatAxisValue}/>,
        timeseries: ()=><Lines label={indicatorLabel} width={width} values={regionData} max={0.08} tickValuesY={[
                0,
                0.02,
                0.04,
                0.06,
                0.08
            ]} target={0.04} staggerDelay={renderMode === "static" ? 0 : undefined} worldFirst={false} showTarget={renderMode !== "embed"}/>
    })}
    </HeroControlsWrapper>;
};
const csv = new URL("../data/data_finance_hero.zip", import.meta.url).href;
const xlsx = new URL("../data/data_finance_hero.xlsx", import.meta.url).href;
const HF = ({ renderMode = "default", dataset, renderMultiple })=>{
    const intl = useIntl();
    const [ref, contentRect] = useContentRect();
    const Renderer = withDataLoader(dataset ? datasets[dataset].url : new URL("../data/data_finance_hero.json", import.meta.url).href, Data, (props)=>contentRect.width === 0 ? null : dataset ? <Chart {...props} width={contentRect.width} csv={datasets[dataset].csv} xlsx={datasets[dataset].xlsx} dataset={dataset}/> : <Chart {...props} width={contentRect.width} csv={csv} xlsx={xlsx} dataset={dataset}/>, {
        narrow: 400,
        wide: 400
    });
    const children = <div ref={ref} {...stylex.props(layout.fullWidthHidden)}>
      <Renderer key="finance" renderMode={renderMode}/>
    </div>;
    return renderMode === "embed" ? <EmbedChart caption={intl.formatMessage(infoLabel)} source={intl.formatMessage(metadata.source)} isHeroChart>
      <HeroStateProvider initial={"today"}>{children}</HeroStateProvider>
    </EmbedChart> : renderMode === "static" ? <StaticChart layout={"columns"} title={metadata.title ? intl.formatMessage(metadata.title) : undefined} caption={intl.formatMessage(metadata.caption)} source={intl.formatMessage(metadata.source)}>
      <HeroStateProvider initial={"today"}>{children}</HeroStateProvider>
      <div {...stylex.props(layout.heroSpacer)}/>
      <HeroStateProvider initial={"timeseries"}>{children}</HeroStateProvider>
    </StaticChart> : renderMultiple ? <MultipleHeroes datasets={datasets} Component={HF}/> : <HeroStateProvider>
      <Hero title={metadata.title ? intl.formatMessage(metadata.title) : undefined} caption={intl.formatMessage(metadata.caption)} targetTitle={intl.formatMessage(targetTitle)} targetLabel={intl.formatMessage(targetLabel)}>
        {children}
      </Hero>
    </HeroStateProvider>;
};
export default HF;
